// MissedMap — 3회 오답 후 "내가 뭘 몰랐을까요?" 지식 맵 (그래프 뷰 스타일).
// 중심 원(이 문제) 둘레에 알아야 했던 개념(주색)·필요했던 태도(민트)를 작은 점으로 잇고,
// 라벨은 점 옆에 쓴다 — 원 안에 글자를 넣으면 영어 라벨이 원 밖으로 삐져나온다.
// 전체 설명은 아래 리스트로 보여준다. 데이터는 parseMissedItems 산출물.
import React from "react";
import { MathText } from "./math.jsx";
import { tr } from "../core/platform.js";

const KIND_COLOR={ "개념":"var(--pri)", "태도":"#27C2A0" };

// SVG <text>는 KaTeX를 못 그리므로 원 안 라벨은 LaTeX 마커만 벗겨 평문으로
function plainMath(s){
  return String(s).replace(/\\[a-zA-Z]+\{([^}]*)\}/g,"$1").replace(/[${}\\]/g,"");
}

/* 중앙 원에는 이 문제의 개념명이 들어간다 — 길면 글자를 줄이고 최대 3줄로 접는다.
   자를 위치는 공백을 우선하고, 공백이 없으면 강제로 나눈다. */
function centerLabel(s){
  const t=plainMath(String(s||"")).trim();
  if(!t)return{lines:["이 문제"],size:12};
  const size=t.length<=6?12:t.length<=13?10:9;
  const per=t.length<=6?7:t.length<=13?8:9;
  const lines=[];
  let rest=t;
  while(rest&&lines.length<3){
    if(rest.length<=per){lines.push(rest);rest="";break;}
    let cut=rest.lastIndexOf(" ",per);
    if(cut<=0)cut=per;
    lines.push(rest.slice(0,cut));
    rest=rest.slice(cut).trim();
  }
  if(rest)lines[2]=lines[2].slice(0,Math.max(1,per-1))+"…";
  return{lines,size};
}

// 라벨을 점 옆에 1~2줄로 — 공백이 있으면 가운데서 가장 가까운 공백에서 자른다
function wrap2(s){
  const t=String(s);
  if(t.length<=9)return[t];
  const mid=Math.ceil(t.length/2);
  let cut=-1;
  for(let i=1;i<t.length-1;i++)if(t[i]===" "&&(cut<0||Math.abs(i-mid)<Math.abs(cut-mid)))cut=i;
  return cut>0?[t.slice(0,cut),t.slice(cut+1)]:[t.slice(0,mid),t.slice(mid)];
}

function MissedMap({center="이 문제",items}){
  if(!items||!items.length)return null;
  const W=380,H=284,cx=W/2,cy=H/2,R=104;
  const nodes=items.map((it,i)=>{
    const a=-Math.PI/2+i*2*Math.PI/items.length;
    return {...it,x:cx+R*Math.cos(a),y:cy+R*0.75*Math.sin(a)};
  });
  return(
    <div className="blk" style={{margin:0}}>
      <div className="bl">{tr("내가 뭘 몰랐을까?","What did I miss?")}</div>
      <div style={{display:"flex",gap:14,justifyContent:"center",margin:"2px 0 4px",fontSize:11.5,color:"var(--sub)"}}>
        {[["개념",tr("부족한 개념","Missing concepts")],["태도",tr("필요한 태도","Needed attitudes")]].map(([k,t])=>(
          <span key={k} style={{display:"inline-flex",alignItems:"center",gap:5}}>
            <span style={{width:9,height:9,borderRadius:"50%",background:KIND_COLOR[k]}}/>{t}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto"}}>
        {nodes.map((n,i)=><line key={"l"+i} x1={cx} y1={cy} x2={n.x} y2={n.y} stroke="var(--line-hi)" strokeWidth="1.5"/>)}
        {nodes.map((n,i)=>{
          const c=KIND_COLOR[n.kind]||KIND_COLOR["개념"], lines=wrap2(plainMath(n.label));
          // 라벨은 점의 사분면에 따라 바깥쪽으로: 좌우 노드는 옆에, 위/아래 노드는 위/아래에
          const right=n.x>=cx+18, left=n.x<=cx-18;
          const tx=right?n.x+10:left?n.x-10:n.x;
          const anchor=right?"start":left?"end":"middle";
          const ty=(right||left)
            ?n.y+4-(lines.length>1?6:0)                       // 옆: 점 높이에 세로 중앙 정렬
            :(n.y<cy?n.y-14-(lines.length-1)*12:n.y+18);      // 위/아래: 점에서 바깥으로
          return(
            <g key={"n"+i}>
              <circle cx={n.x} cy={n.y} r={5.5} fill={c}/>
              <text x={tx} y={ty} textAnchor={anchor} fontSize="11" fontWeight="700" fill={c}>
                {lines.map((s,k)=><tspan key={k} x={tx} dy={k?12:0}>{s}</tspan>)}
              </text>
            </g>);
        })}
        <circle cx={cx} cy={cy} r={33} fill="var(--pri)"/>
        {(()=>{const C=centerLabel(center);const lh=C.size+1.5;
          return(
            <text x={cx} y={cy+C.size*0.36-(C.lines.length-1)*lh/2} textAnchor="middle"
              fontSize={C.size} fontWeight="800" fill="#fff">
              {C.lines.map((s,k)=><tspan key={k} x={cx} dy={k?lh:0}>{s}</tspan>)}
            </text>);
        })()}
      </svg>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:10}}>
        {items.map((it,i)=>(
          <div key={i} style={{fontSize:12.5,lineHeight:1.55,color:"var(--ink)"}}>
            <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",
              background:KIND_COLOR[it.kind]||KIND_COLOR["개념"],marginRight:6}}/>
            <b><MathText text={it.label} tag="span"/></b>
            {it.desc?<> — <MathText text={it.desc} tag="span"/></>:null}
          </div>
        ))}
      </div>
    </div>
  );
}

export { MissedMap };
